import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useApiClient from '../api/useApiClient';
import useAuth from '../auth/useAuth';
import useBrand from '../context/useBrand';
import { useGetPlatformAgentContextQuery, useGetPlatformAgentHealthQuery } from '../../store/api-slices';
import { selectCurrentFranchiseId } from '../../store/slices/CurrentFranchiseSlice';
import {
  isPlatformAgentHealthy,
  platformAgentName,
  platformAgentSubjectKey,
} from '../../services/platform-agent/platformAgent';

// A recovered agent turns the button back on within a minute.
const HEALTH_POLLING_MS = 60_000;

/**
 * The platform agent (Amigo) for the restaurant in context:
 * - `available`: shop's context says the person gets Amigo here (contracts §2). False while it loads, on a 404 or
 *   any error, so the button only shows once shop said yes.
 * - `unavailable`: available, but the agent can't be reached (no URL configured, or its health check fails).
 * - `launcher`: the bottom-right row stays (with Intercom's companion launcher) — available, or the next
 *   restaurant's context still loading after a yes.
 * - `subjectKey`: the chat's key in the platformAgent slice; `name`: Amigo or the assistant (platformAgentName).
 * - `requestHeaders`: the headers shop gets from this dashboard (the login, on-behalf-of, the language), which the
 *   panel's chat sends to the agent too (contracts §1).
 * - `chatsManager`, `filesManager`: the agent's chats (§6) and their files (§8 GET /files, a chat's history) with
 *   those headers; null without an agent URL.
 * - `checkHealth()`: asks the agent's health again now, not at the next poll (a message that couldn't reach it, QA48);
 *   resolves once it answered.
 * Neither call blocks anything: RTK Query runs them in the background.
 */
export default function usePlatformAgent() {
  const agentUrl = import.meta.env.VITE_PLATFORM_AGENT_URL;

  const { PlatformAgentManager, config } = useApiClient();
  const { PlatformAgentHealthManager, PlatformAgentChatsManager, PlatformAgentFilesManager } = useApiClient({
    baseUrl: agentUrl,
    autoLogOut: false,
  });
  const { isLoggedIn } = useAuth();
  const brand = useBrand();

  const companyId = useSelector((state) => state.currentCompany.company?.id ?? null);
  const franchiseId = useSelector(selectCurrentFranchiseId) ?? null;

  // `currentData`, not `data`: while another restaurant's context loads, the last one's must not show.
  const {
    currentData: context,
    isError: contextFailed,
    isFetching: contextLoading,
  } = useGetPlatformAgentContextQuery(
    { apiManager: PlatformAgentManager, companyId, franchiseId },
    { skip: !isLoggedIn || (!companyId && !franchiseId) },
  );

  const available = !contextFailed && context?.available === true;

  // While the next restaurant's context loads, `available` is false until shop answers. The bottom-right row keeps
  // Intercom's companion launcher meanwhile if the last answer was yes, so Intercom's own doesn't flash in and out.
  const [lastAnswer, setLastAnswer] = useState(false);

  useEffect(() => {
    if (!contextLoading) {
      setLastAnswer(available);
    }
  }, [contextLoading, available]);

  const launcher = available || (contextLoading && lastAnswer);

  const healthSkipped = !available || !agentUrl;
  const {
    data: health,
    isSuccess: healthAnswered,
    isError: healthFailed,
    refetch: refetchHealth,
  } = useGetPlatformAgentHealthQuery(
    { apiManager: PlatformAgentHealthManager },
    { skip: healthSkipped, pollingInterval: HEALTH_POLLING_MS },
  );

  // RTK Query refuses to refetch a query it skips: without Amigo there is nothing to check.
  const checkHealth = useCallback(async () => {
    if (!healthSkipped) {
      await refetchHealth();
    }
  }, [healthSkipped, refetchHealth]);

  // Before the first answer the agent counts as reachable, so the button doesn't flash muted on every load. An
  // unreachable agent (no response, so no body) resolves as a "success" without data in baseQuery, so anything but
  // `{ ok: true }` counts as down.
  const unavailable = available && (!agentUrl || healthFailed || (healthAnswered && !isPlatformAgentHealthy(health)));

  const subjectKey = platformAgentSubjectKey(context?.restaurant?.subject);
  const name = platformAgentName(brand);

  const requestHeaders = config.headers;
  const chatsManager = agentUrl ? PlatformAgentChatsManager : null;
  const filesManager = agentUrl ? PlatformAgentFilesManager : null;

  return useMemo(
    () => ({
      available,
      unavailable,
      launcher,
      context: available ? context : null,
      subjectKey,
      name,
      requestHeaders,
      chatsManager,
      filesManager,
      checkHealth,
    }),
    [
      available,
      unavailable,
      launcher,
      context,
      subjectKey,
      name,
      requestHeaders,
      chatsManager,
      filesManager,
      checkHealth,
    ],
  );
}
